import { DownloadSimpleIcon, MagnifyingGlassIcon, PaperPlaneTiltIcon } from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import React, { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { useDownloadClientsListCvs } from '@/api/clients/download-clients-list-cvs'
import {
  BASE_CLIENT_DASHBOARD_QUERY_KEY,
  GetClientsDashboardRequest,
  getClientsDashboard,
  getClientsDashboardQueryKey,
  useGetClientsDashboard,
} from '@/api/clients/get-clients-dashboard'
import { usePersonColumnSettings } from '@/components/business/person-column'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Select } from '@/components/ui/select'
import { StatusTabOption, StatusTabs } from '@/components/ui/status-tabs'
import { BulkAction, RowAction, SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { PopupWithRequiredProps, popupFactory } from '@/factories'
import { useCaptureEvent, useFilters, useSearchDraft, useSearchParams } from '@/hooks'
import { ClientModel } from '@/models/features/clients'
import { UserRoleEnum } from '@/models/features/user.models'
import { SendAppInviteModal } from '@/pages/clients/send-app-invite-modal'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectUserRole } from '@/reducers/userDataReducer'
import { buildCsvFilename } from '@/utils/file'

import {
  getClientsTableFilterTabs,
  getPaginationTotal,
  isClientAppUser,
} from './client-table.page.utils'
import {
  APP_USER_CUTOFF_DATE_MS,
  CLIENTS_ID,
  CLIENTS_PMS_CONNECTED_ID,
  clientsTableInitialFilters,
  clientsTableInitialQueryFiltersSchema,
  getAppUserOptions,
  getClientsTableColumns,
  NON_PMS_ALLOWED_STATUSES,
  PMS_ALLOWED_STATUSES,
} from './clients-table.constants'
import {
  AppUserFilter,
  ClientFilterStatus,
  NonPMSConnectedBuildingStatusEnum,
  PMSConnectedBuildingStatusEnum,
} from './clients-table.types'

export const ClientsTablePage = () => {
  const { t } = useTranslation(['clients'])
  const selectedBuilding = useSelector(selectActiveBuilding)
  const userRole = useSelector(selectUserRole)
  const navigate = useNavigate()
  const { captureEvent, captureDebouncedEvent } = useCaptureEvent()
  const queryClient = useQueryClient()

  const selectedBuildingId = selectedBuilding?.id
  const isPMSConnectedBuilding = selectedBuilding?.PMS
  const currentClientsTableId = isPMSConnectedBuilding ? CLIENTS_PMS_CONNECTED_ID : CLIENTS_ID
  const showAppUserStatus = Boolean(selectedBuilding?.appUserStatusAvailable)

  const searchParams = useSearchParams({
    initialState: clientsTableInitialFilters,
    schema: clientsTableInitialQueryFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: clientsTableInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const appUser = filters.getFilter('appUser')
  const limit = filters.getFilter('limit')
  const [searchDraft, setSearchDraft] = useSearchDraft(filters.getFilter('search'), (value) => {
    filters.setFilter('search', value)
    captureDebouncedEvent('user_action', {
      name: 'search_change',
      screen: currentClientsTableId,
      value,
    })
  })
  const search = searchDraft.trim()
  const skip = filters.getFilter('skip')
  const status = filters.getFilter('status')

  // Note: Backend does not support ALL status, but it is necessary on the FE side.
  // Guarding against the race window where building type changed but the status effect hasn't run yet.
  const allowedStatuses = (
    isPMSConnectedBuilding ? PMS_ALLOWED_STATUSES : NON_PMS_ALLOWED_STATUSES
  ) as ClientFilterStatus[]
  const isAllStatus = status === PMSConnectedBuildingStatusEnum.enum.all
  const isStatusOutOfSync = !status || !allowedStatuses.includes(status)
  const statusParam = isAllStatus || isStatusOutOfSync ? undefined : status

  const sortBy = filters.getFilter('sortBy')
  const sortOrder = filters.getFilter('sortOrder')

  // Note: BE only accepts a single boolean, so "Yes" + "No" both selected (or neither) means
  // no filtering rather than defaulting to one of the two values.
  const appUsersParam = appUser?.length === 1 ? appUser[0] === 'yes' : undefined

  const { data, isFetching } = useGetClientsDashboard(
    {
      appUsers: appUsersParam,
      buildingId: selectedBuildingId,
      limit,
      search,
      skip,
      sortBy,
      sortOrder,
      status: statusParam,
    },
    {
      select: (data) => {
        if (!selectedBuilding) return data

        return {
          ...data,
          clients: data.clients.map((client) => ({
            ...client,
            buildingId: {
              code: selectedBuilding.code ?? '',
              dormakabaAvailable: selectedBuilding.dormakabaAvailable,
              id: selectedBuilding.id,
              iglooWorkAvailable: selectedBuilding.iglooWorkAvailable,
              name: selectedBuilding.name ?? '',
              PMS: selectedBuilding.PMS,
              remoteLockAvailable: selectedBuilding.remoteLockAvailable,
              saltoSpaceAvailable: selectedBuilding.saltoSpaceAvailable,
              swiftlaneAvailable: selectedBuilding.swiftlaneAvailable,
              timezone: selectedBuilding.timezone ?? '',
            },
            isAppUser: isClientAppUser(client.lastAppLogin, APP_USER_CUTOFF_DATE_MS),
          })),
        }
      },
    },
  )

  const total = getPaginationTotal(data, status)

  const resolveAllMatchingClients = async (signal?: AbortSignal): Promise<ClientModel[]> => {
    if (!selectedBuildingId || !total) return []

    const request: GetClientsDashboardRequest = {
      appUsers: appUsersParam,
      buildingId: selectedBuildingId,
      limit: total,
      search,
      skip: 0,
      sortBy,
      sortOrder,
      status: statusParam,
    }

    const response = await queryClient.fetchQuery({
      queryFn: ({ signal: querySignal }) =>
        getClientsDashboard({ data: request, signal: querySignal ?? signal }),
      queryKey: getClientsDashboardQueryKey(request),
    })

    return response.clients.map((client) => ({
      ...client,
      isAppUser: isClientAppUser(client.lastAppLogin, APP_USER_CUTOFF_DATE_MS),
    }))
  }

  const { mutateAsync: downloadClientsList, isPending: isDownloadPending } =
    useDownloadClientsListCvs()

  const handleDownloadCSVClick = () => {
    if (!selectedBuildingId) return

    const fileName = buildCsvFilename({
      building: selectedBuilding?.name || null,
      date: new Date(),
      page: 'clients',
      timezone: selectedBuilding?.timezone,
    })
    downloadClientsList({ buildingId: selectedBuildingId, fileName }).catch(() => {})

    captureEvent('user_action', {
      name: 'clients_csv_download_click',
      screen: currentClientsTableId,
    })
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchDraft(e.target.value)
  }

  const handleStatusChange = (status: ClientFilterStatus) => {
    filters.setFilter('status', status)
    captureEvent('user_action', {
      name: 'status_change',
      screen: currentClientsTableId,
      value: status,
    })
  }

  const statusOptions: StatusTabOption<ClientFilterStatus>[] = getClientsTableFilterTabs({
    data,
    isPMSConnectedBuilding,
    t,
  })

  const appUserOptions = getAppUserOptions(t)

  const residentColumnSettings = usePersonColumnSettings({
    column: 'resident',
    dashboard: 'Contacts',
    fieldType: 'resident',
    displays: (data?.clients ?? []).map((client) => client.residentDisplay),
    rowsQueryKey: [BASE_CLIENT_DASHBOARD_QUERY_KEY],
  })

  const tableColumns = getClientsTableColumns(
    t,
    selectedBuilding?.timezone ?? '',
    isPMSConnectedBuilding,
    showAppUserStatus,
    residentColumnSettings,
  )

  const rowActions: RowAction<ClientModel>[] = [
    {
      label: t('clients:list.actions.view'),
      onClick: (item) => {
        navigate(`${item.id}`)
      },

      primary: true,
    },
  ]

  const handleAppUserChange = (values: AppUserFilter[]) => {
    filters.setFilter('appUser', values.length ? values : undefined)
    captureEvent('user_action', {
      name: 'app_user_filter_change',
      screen: currentClientsTableId,
      value: values,
    })
  }

  const bulkActions: BulkAction<ClientModel>[] =
    showAppUserStatus &&
    (userRole === UserRoleEnum.enum.PropertyManager || userRole === UserRoleEnum.enum.Administrator)
      ? [
          {
            icon: PaperPlaneTiltIcon,
            label: t('clients:list.actions.sendAppInvite'),
            onClick: (items) => {
              if (!selectedBuildingId) return

              popupFactory.open(SendAppInviteModal as FC<PopupWithRequiredProps>, {
                buildingId: selectedBuildingId,
                selectedClients: items,
              })
            },
          },
        ]
      : []

  const handleSort = (sortBy: string | null, sortOrder: SortOrder | null) => {
    filters.setFilters({
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
    })
    captureEvent('user_action', {
      name: 'sort_change',
      screen: currentClientsTableId,
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
    })
  }

  // Note: Add initial filters on component mount
  // Do it only if there are no initial filters passed from the URL
  useEffect(() => {
    if (searchParams.parsedQuery.sortBy || searchParams.parsedQuery.sortOrder) {
      return
    }

    filters.setFilters({
      sortBy: 'moveInDate',
      sortOrder: 'desc',
    })
  }, [])

  // Note: Set tab filter depending on selected building (PMS or non-PMS connected)
  // Skip if a status already valid for this building type is present (e.g. restored
  // from the URL on back-navigation) so we don't stomp it back to the default on mount
  useEffect(() => {
    if (!isStatusOutOfSync || isAllStatus) {
      return
    }

    const nextStatus = isPMSConnectedBuilding
      ? PMSConnectedBuildingStatusEnum.enum.all
      : NonPMSConnectedBuildingStatusEnum.enum.active

    filters.setFilter('status', nextStatus)
  }, [isPMSConnectedBuilding])

  return (
    <TableGroup
      // Note: Passing a different key will ensure that initial visible columns will change based on selected building
      key={currentClientsTableId}
      id={currentClientsTableId}
      data={data?.clients ?? []}
      loading={isFetching}
      columns={tableColumns}
      mode={bulkActions.length > 0 ? 'bulk' : 'regular'}
      bulkActions={bulkActions}
      selectAllMatching={{ resolveAll: resolveAllMatchingClients, total }}
      getRowKey={(client) => client.id}
      filters={
        showAppUserStatus && (
          <Select
            type="multi"
            options={appUserOptions}
            placeholder={t('clients:list.appUser.placeholder')}
            size="small"
            value={appUser ?? []}
            onChange={handleAppUserChange}
            onReset={() => handleAppUserChange([])}
          />
        )
      }
      navigation={{
        actions: (
          <Button
            onlyBox
            use="secondary"
            size="small"
            title={t('clients:list.actions.exportCsv')}
            disabled={!selectedBuildingId || isDownloadPending || isFetching}
            onClick={handleDownloadCSVClick}
          >
            <Icon icon={DownloadSimpleIcon} />
          </Button>
        ),
        search: (
          <TextInput
            clearable
            icon={MagnifyingGlassIcon}
            placeholder={t('clients:list.search.placeholder')}
            size="small"
            value={searchDraft}
            onChange={handleSearchChange}
          />
        ),
        statusTabs: (
          <StatusTabs<ClientFilterStatus>
            options={statusOptions}
            value={filters.getFilter('status') ?? null}
            onChange={handleStatusChange}
          />
        ),
      }}
      pagination={{
        paginationFilters: filters,
        total,
      }}
      tableFilters={filters}
      rowActions={rowActions}
      sortBy={filters.getFilter('sortBy')}
      sortOrder={filters.getFilter('sortOrder')}
      onSort={handleSort}
    />
  )
}
